import { useEffect, useRef, useState } from 'react';

/**
 * Animates a number from `start` -> `end` once the element scrolls into view
 * (or immediately if `immediate`). Returns [ref, value]. rAF-based, eases out.
 * Respects prefers-reduced-motion.
 */
const useCountUp = (end, { duration = 1500, start = 0, decimals = 0, immediate = false } = {}) => {
  const ref = useRef(null);
  const [value, setValue] = useState(start);
  const done = useRef(false);

  useEffect(() => {
    const node = ref.current;
    const reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setValue(end);
      return undefined;
    }

    const run = () => {
      if (done.current) return;
      done.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - t0) / duration);
        const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
        const current = start + (end - start) * eased;
        setValue(decimals ? parseFloat(current.toFixed(decimals)) : Math.round(current));
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    if (immediate || !node) {
      run();
      return undefined;
    }

    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { run(); io.disconnect(); } }),
      { threshold: 0.4 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [end, duration, start, decimals, immediate]);

  return [ref, value];
};

export default useCountUp;
